import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import {
  fetchPartsForecast,
  fetchPartsForecastReport,
  generatePartsForecast,
  type PartsForecastItem,
} from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatDateTime } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useReportFilters } from '@/pages/reports/report-filters-context.js';

export function PartsForecastReportPage() {
  const f = useReportFilters();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { reportId } = useParams<{ reportId?: string }>();
  const storeId = f.storeId.trim() || undefined;

  const q = useQuery({
    queryKey: ['reports-parts-forecast', storeId, reportId ?? 'latest'],
    queryFn: async () => {
      if (reportId) {
        const report = await fetchPartsForecastReport(reportId);
        const result = report.result ?? {};
        const items: PartsForecastItem[] = Array.isArray(result.items) ? result.items : [];
        return {
          generatedAt: result.generatedAt ?? report.completedAt ?? report.createdAt,
          items,
          status: report.status,
          reportId: report.id,
        };
      }
      return fetchPartsForecast({ storeId });
    },
  });

  const generateMutation = useMutation({
    mutationFn: () => generatePartsForecast({ storeId }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reports-parts-forecast'] });
      toastSuccess('Parts forecast regenerated');
      if (data.reportId) {
        navigate(`/reports/parts-forecast/${data.reportId}`, { replace: true });
      }
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const d = q.data;
  const items = d?.items ?? [];

  const csv = () => {
    exportToCsv(
      items.map((i) => ({
        name: i.name,
        sku: i.sku ?? '',
        avgWeeklyUsage: i.avgWeeklyUsage,
        currentStock: i.currentStock,
        daysUntilStockout: i.daysUntilStockout ?? '',
        suggestedReorderQty: i.suggestedReorderQty,
        suggestedReorderDate: i.suggestedReorderDate ?? '',
      })),
      'parts-forecast.csv',
    );
  };

  if (q.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading parts forecast…</div>;
  }
  if (q.isError) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">
        Could not load parts forecast.
        {reportId ? (
          <button
            type="button"
            className="mt-3 block text-sm font-semibold underline"
            onClick={() => navigate('/reports/parts-forecast')}
          >
            Open latest forecast
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          {d?.generatedAt ? `Last generated ${formatDateTime(d.generatedAt)}` : 'No forecast generated yet.'}
          {d?.reportId ? (
            <span className="ml-2 font-mono text-xs text-slate-400">#{d.reportId.slice(0, 8)}</span>
          ) : null}
        </p>
        <div className="flex gap-2">
          <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
            Export CSV
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-60"
            disabled={generateMutation.isPending}
            onClick={() => generateMutation.mutate()}
          >
            <RefreshCw className={`h-4 w-4 ${generateMutation.isPending ? 'animate-spin' : ''}`} />
            {generateMutation.isPending ? 'Generating…' : 'Regenerate Forecast'}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
            <tr>
              <th className="px-4 py-3">Part</th>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Avg weekly usage</th>
              <th className="px-4 py-3">Current stock</th>
              <th className="px-4 py-3">Days until stockout</th>
              <th className="px-4 py-3">Suggested reorder qty</th>
              <th className="px-4 py-3">Reorder by</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const urgent = item.daysUntilStockout != null && item.daysUntilStockout <= 7;
              return (
                <tr key={item.itemId} className={`border-b border-slate-100 ${urgent ? 'bg-red-50/60' : ''}`}>
                  <td className="px-4 py-2 font-medium">{item.name}</td>
                  <td className="px-4 py-2 text-slate-500">{item.sku ?? '—'}</td>
                  <td className="px-4 py-2">{item.avgWeeklyUsage.toFixed(1)}</td>
                  <td className="px-4 py-2">{item.currentStock}</td>
                  <td className={`px-4 py-2 font-semibold ${urgent ? 'text-red-600' : ''}`}>
                    {item.daysUntilStockout ?? '—'}
                  </td>
                  <td className="px-4 py-2">{item.suggestedReorderQty}</td>
                  <td className="px-4 py-2 text-slate-500">
                    {item.suggestedReorderDate ? formatDateTime(item.suggestedReorderDate) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!items.length ? (
          <p className="p-6 text-center text-slate-500">
            No forecast data yet. Click &quot;Regenerate Forecast&quot; to build one from recent usage.
          </p>
        ) : null}
      </div>
    </div>
  );
}
